import type { DriveStep } from 'driver.js';

/**
 * Walkthrough definitions per page. Each walkthrough is a short, opinionated
 * tour that explains *what the section does, why it matters, and how
 * TikTok Shop agencies actually use it.*
 *
 * Element selectors target `[data-tour="..."]` attributes that the layout
 * and pages add to key UI elements. Steps without `element` render as a
 * centered popover (full-screen).
 */
export type WalkthroughKey =
  | 'dashboard'
  | 'brands'
  | 'creators'
  | 'live-ops'
  | 'campaigns'
  | 'reports'
  | 'content'
  | 'settings'
  | 'brand-portal';

export interface WalkthroughDefinition {
  key: WalkthroughKey;
  title: string;
  routePrefix: string;
  steps: DriveStep[];
}

const popover = (title: string, description: string) => ({ title, description });

export const walkthroughs: Record<WalkthroughKey, WalkthroughDefinition> = {
  dashboard: {
    key: 'dashboard',
    title: 'Dashboard',
    routePrefix: '/dashboard',
    steps: [
      {
        popover: popover(
          'Welcome to your command center',
          'The Dashboard is the operational pulse of your agency — GMV, LIVE volume, creator activity, and brand health all in one place. Top agencies open this first every morning.'
        ),
      },
      {
        element: '[data-tour="dashboard-stats"]',
        popover: popover(
          'Daily operating metrics',
          'These are the numbers that should move every day. Treat anything flat for 48 hours as a signal to investigate.'
        ),
      },
      {
        element: '[data-tour="nav-brands"]',
        popover: popover(
          'Brands live here',
          'Every active client is tracked under Brands — contracts, creators, GMV, and portal access. Add your first brand from there next.'
        ),
      },
    ],
  },
  brands: {
    key: 'brands',
    title: 'Brands',
    routePrefix: '/brands',
    steps: [
      {
        popover: popover(
          'Brands are the unit of work',
          'A brand in FlowTok OS represents a single TikTok Shop client. Everything — creators, LIVEs, samples, Spark Codes, content, reporting — rolls up to a brand.'
        ),
      },
      {
        element: '[data-tour="brands-add"]',
        popover: popover(
          'Add a brand',
          'Use this to onboard a new client. Set the retainer, GMV split, and category so reporting and revenue tracking are accurate from day one.'
        ),
      },
      {
        element: '[data-tour="brands-filters"]',
        popover: popover(
          'Filter by lifecycle stage',
          'Active, Onboarding, Paused, Churned, and Archived are real lifecycle stages — not labels. Brands flow between them as engagements evolve.'
        ),
      },
      {
        popover: popover(
          'Open any brand for the full portal',
          'Inside a brand you get health score, creators, LIVE pipeline, samples, growth plan, and the shareable client portal link.'
        ),
      },
    ],
  },
  creators: {
    key: 'creators',
    title: 'Creators',
    routePrefix: '/creators',
    steps: [
      {
        popover: popover(
          'Your creator roster',
          'Creators (affiliates) are the engine of TikTok Shop GMV. This is where you track every active partnership across all brands.'
        ),
      },
      {
        popover: popover(
          'Tier, commission, samples',
          'Tag creators by tier and assign commission. Sample tracking and Spark Code attribution are linked back to the creator record automatically.'
        ),
      },
    ],
  },
  'live-ops': {
    key: 'live-ops',
    title: 'LIVE Ops',
    routePrefix: '/live-sessions',
    steps: [
      {
        popover: popover(
          'LIVE Sessions',
          'Every TikTok LIVE — past, scheduled, in-progress — lives here. This is the room your hosts and managers should run from.'
        ),
      },
      {
        popover: popover(
          'Pre-LIVE → LIVE → Post-LIVE',
          'Each session has structured fields for prep, performance, and recap. Top agencies fill out post-LIVE within 24h to compound their data advantage.'
        ),
      },
    ],
  },
  campaigns: {
    key: 'campaigns',
    title: 'Spark Codes & Campaigns',
    routePrefix: '/spark-codes',
    steps: [
      {
        popover: popover(
          'Spark Codes are paid amplification',
          'When a creator post performs, you boost it with a Spark Code. Track budget, spend, and expiry here so nothing leaks.'
        ),
      },
      {
        popover: popover(
          'Budget & status alerts',
          'Codes nearing expiry or budget cap surface automatically — agencies running 6-figure ad spend treat this as a daily checkpoint.'
        ),
      },
    ],
  },
  reports: {
    key: 'reports',
    title: 'Intelligence & Reports',
    routePrefix: '/intelligence',
    steps: [
      {
        popover: popover(
          'FlowTok Intelligence',
          'Cross-brand benchmarks, GMV trends, and LIVE timing recommendations. This is where insights compound across your entire client book.'
        ),
      },
      {
        popover: popover(
          'How agencies use this',
          'Use Intelligence in your weekly client reviews — it turns raw data into a story your brand contacts can act on.'
        ),
      },
    ],
  },
  content: {
    key: 'content',
    title: 'Content Library',
    routePrefix: '/content-library',
    steps: [
      {
        popover: popover(
          'Centralized creative library',
          'Every video, hook, brief, and approved asset — in one place, tagged by brand and campaign.'
        ),
      },
      {
        popover: popover(
          'Why it matters',
          'Reusable winners (top 10% by GMV) become the playbook your team scales across new creators and new brands.'
        ),
      },
    ],
  },
  settings: {
    key: 'settings',
    title: 'Settings',
    routePrefix: '/settings',
    steps: [
      {
        popover: popover(
          'Personal & agency settings',
          'Manage your profile, agency branding, billing, and team here. You can also restart any walkthrough from this page.'
        ),
      },
    ],
  },
  'brand-portal': {
    key: 'brand-portal',
    title: 'Brand Portal',
    routePrefix: '/portal/',
    steps: [
      {
        popover: popover(
          'The client-facing view',
          "This is what brand contacts see when you share their portal link. They get a curated, on-brand operations dashboard — without seeing your other clients."
        ),
      },
    ],
  },
};

export function walkthroughForRoute(pathname: string): WalkthroughDefinition | null {
  // Most-specific match first
  const sorted = Object.values(walkthroughs).sort(
    (a, b) => b.routePrefix.length - a.routePrefix.length,
  );
  return sorted.find((w) => pathname.startsWith(w.routePrefix)) ?? null;
}
